import React from "react";
import { parseDepth } from "../../../utils/commonComponent/parse";
import { InwardPDfColumn } from "../../../global";

const InwardTestTableCommom = ({ testRows = [], isCSRF }) => {
  // Group tests by sample details
  const groupedTests = React.useMemo(() => {
    const expandedRows = [];
    testRows.forEach((row) => {
      const testIds = row.testId
        ? String(row.testId)
            .split(",")
            .map((id) => id.trim())
        : [""];
      testIds.forEach((testId) => {
        const testDetail = row.testDetails?.[testId];
        //  Resolve this specific test's material name using testDetail.materialId
        const testMaterialId = testDetail?.materialId
          ? String(testDetail.materialId)
          : null;

        // materialNamesMap is passed via row (resolved in InwardView)
        const testMaterialName = testMaterialId && row.materialNamesMap
          ? row.materialNamesMap[testMaterialId] ?? row.materialName ?? ""
          : row.materialName ?? "";

        expandedRows.push({
          ...row,
          testId: testId,
          testName: testDetail?.testName || row.testName || "",
          testMethod: testDetail?.testMethod || row.testMethod || "",
          //  Each expanded row gets its own specific material name
          resolvedMaterialName: testMaterialName,
          muValue: testDetail?.muValue || "",
          testValue: testDetail?.testValue || "",
        });
      });
    });

    const grouped = [];
    const map = {};

    expandedRows.forEach((test) => {
      const sampleKey = `${test.locationChainageNo}_${test.boreHoleNo}_${test.fromDepth}_${test.toDepth}_${test.sampleType}_${test.quantity}_${test.lengthOfRock}_${test.materialName}`;

      if (!map[sampleKey]) {
        map[sampleKey] = [];
      }
      map[sampleKey].push(test);
    });

    Object.values(map).forEach((group) => {
      group.forEach((test, idx) => {
        grouped.push({
          ...test,
          _rowSpan: group.length,
          _isFirst: idx === 0,
        });
      });
    });

    return grouped;
  }, [testRows]);

  // Calculate material groups for matrix layout
  const { materialOrder, materialsMap, sampleGroups } = React.useMemo(() => {
    const flat = groupedTests;
    const materialsMap = {};
    const materialOrder = [];
    const groups = {};

    flat.forEach((entry) => {
      //  Use resolvedMaterialName (per-test specific material) instead of splitting materialName
      const mat = entry.resolvedMaterialName || entry.materialName || "Type of sample";
      const testLabel = `${entry.testName || ""}${
        entry.testMethod ? ` (${entry.testMethod})` : ""
      }`.trim();

      if (!materialsMap[mat]) {
        materialsMap[mat] = [];
        materialOrder.push(mat);
      }
      if (testLabel && !materialsMap[mat].includes(testLabel)) {
        materialsMap[mat].push(testLabel);
      }

      const sampleKey = `${entry.locationChainageNo || ""}||${
        entry.boreHoleNo || ""
      }||${entry.fromDepth || ""}||${entry.toDepth || ""}||${
        entry.sampleType || ""
      }||${entry.quantity || ""}||${entry.lengthOfRock || ""}`;

      if (!groups[sampleKey]) groups[sampleKey] = [];
      groups[sampleKey].push({
        ...entry,
        _resolvedMat: mat, //  store the resolved single material for this test
      });
    });

    const sampleGroups = Object.values(groups);
    return { materialOrder, materialsMap, sampleGroups };
  }, [groupedTests]);

  // Calculate total test count
  const totalTestIdCount = React.useMemo(() => {
    return testRows.reduce(
      (sum, row) =>
        sum + (row.testId ? row.testId.split(",").filter(Boolean).length : 0),
      0
    );
  }, [testRows]);

  const uniqueTestIdCount = React.useMemo(() => {
    const allTestIds = new Set();
    testRows.forEach((row) => {
      if (row.testId) {
        row.testId.split(",").forEach((id) => allTestIds.add(id.trim()));
      }
    });
    return allTestIds.size;
  }, [testRows]);

  const uniqueTests = React.useMemo(() => {
    const seen = new Set();
    return groupedTests.filter((test) => {
      if (seen.has(test.testId)) return false;
      seen.add(test.testId);
      return true;
    });
  }, [groupedTests]);

  // Render vertical layout (when many columns)
  const renderVerticalLayout = () => (
    <div className="w-full overflow-x-auto">
      <table className="w-full border border-black border-collapse text-center table-auto">
        <thead>
          <tr>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">Sr.No.</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">
              Location/Chainage No./Source.
            </th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">
              Sample ID / BH No. / Trial Pit No.
            </th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">Depth</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">Sample Type</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">Qty</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">
              Length Of Rock
            </th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">
              Type of sample
            </th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1 max-w-[140px]">
              Tests
            </th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1 max-w-[140px]">
              Ref standards
            </th>
          </tr>
        </thead>

        <tbody>
          {(() => {
            let srNo = 1;

            return groupedTests.map((test, index) => (
              <tr key={`${test.id || "row"}-${test.testId || "test"}-${index}`}>
                {test._isFirst && (
                  <>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {srNo++}
                    </td>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {test?.locationChainageNo}
                    </td>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {test?.boreHoleNo}
                    </td>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {parseDepth(test?.fromDepth, test?.toDepth)}
                    </td>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {test?.sampleType?.replaceAll("||", ", ")}
                    </td>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {test?.quantity}
                    </td>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {parseDepth(test?.lengthOfRock)}
                    </td>
                    <td className="border border-black p-1" rowSpan={test._rowSpan}>
                      {test.materialName}
                    </td>
                  </>
                )}

                <td className="border border-black p-1 break-words max-w-[140px]">
                  {test.testName}
                </td>

                <td className="border border-black p-1 break-words max-w-[140px]">
                  {test.testMethod}
                </td>
              </tr>
            ));
          })()}
        </tbody>
      </table>
    </div>
  );

  // Render matrix layout (when fewer columns)
  const renderMatrixLayout = () => (
    <div className="w-full overflow-x-auto">
      <table className="w-full border border-black border-collapse text-center table-auto">
        <thead>
          <tr>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1" rowSpan={2}>Sr.No.</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1" rowSpan={2}>
              Location/Chainage No./Source.
            </th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1" rowSpan={2}>
              Sample ID / BH No. / Trial Pit No.
            </th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1" rowSpan={2}>Depth</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1" rowSpan={2}>Sample Type</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1" rowSpan={2}>Qty</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1" rowSpan={2}>Length Of Rock</th>

            {materialOrder.map((material) => {
              const tests = materialsMap[material] || [];

              return (
                <th
                  key={`mat-${material}`}
                  className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1"
                  colSpan={Math.max(1, tests.length)}
                >
                  {material}
                </th>
              );
            })}
          </tr>

          <tr>
            {materialOrder.map((material) =>
              (materialsMap[material] || []).map((testLabel, i) => (
                <th
                  key={`mat-${material}-test-${i}`}
                  className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1 max-w-[140px] text-xs"
                >
                  {testLabel}
                </th>
              ))
            )}
          </tr>
        </thead>

        <tbody>
          {sampleGroups.map((group, rowIndex) => {
            const first = group[0] || {};
            const present = {};

            group.forEach((e) => {
              //  Use _resolvedMat (per-test specific material) for correct column mapping
              const mat = e._resolvedMat || e.materialName || "Type of sample";
              const label = `${e.testName || ""}${
                e.testMethod ? ` (${e.testMethod})` : ""
              }`.trim();

              if (!present[mat]) present[mat] = new Set();
              if (label) present[mat].add(label);
            });

            return (
              <tr key={`sample-${rowIndex}`}>
                <td className="border border-black p-1">{rowIndex + 1}</td>

                <td className="border border-black p-1">
                  {first.locationChainageNo}
                </td>

                <td className="border border-black p-1">{first.boreHoleNo}</td>

                <td className="border border-black p-1">
                  {parseDepth(first.fromDepth, first.toDepth)}
                </td>

                <td className="border border-black p-1">
                  {first.sampleType?.replaceAll("||", ", ")}
                </td>

                <td className="border border-black p-1">{first.quantity}</td>

                <td className="border border-black p-1">
                  {parseDepth(first.lengthOfRock)}
                </td>

                {materialOrder.map((material) =>
                  (materialsMap[material] || []).map((testLabel, i) => {
                    const has = present[material] && present[material].has(testLabel);
                    return (
                      <td
                        key={`r${rowIndex}-m-${material}-t-${i}`}
                        className="border border-black p-1"
                      >
                        {has ? "✔" : "-"}
                      </td>
                    );
                  })
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderCSRFLayout = () => (
    <div className="w-full overflow-x-auto">
      <table className="w-full border border-black border-collapse text-center table-auto">
        <thead>
          <tr>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">S.No.</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">Test Parameters</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">Unit</th>
            <th className="border border-black p-1 break-words whitespace-normal align-middle text-[11px] leading-tight px-1 py-1">Test Method</th>
          </tr>
        </thead>
        <tbody>
          {uniqueTests.map((test, index) => (
            <tr key={`csrf-${test.id || "row"}-${test.testId || "test"}-${index}`}>
              <td className="border border-black p-1">{index + 1}</td>
              <td className="border border-black p-1 text-left">{test.testName}</td>
              <td className="border border-black p-1">
                {test.muValue?.includes("#")
                  ? test.muValue.split("#")[1]
                  : test.testValue?.includes("#")
                    ? test.testValue.split("#")[1]
                    : ""}
              </td>
              <td className="border border-black p-1">{test.testMethod || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (testRows.length === 0) {
    return (
      <div className="w-full border border-black p-4 text-center text-gray-500">
        No test data available
      </div>
    );
  }

  return isCSRF ? renderCSRFLayout() : renderMatrixLayout();

  // return uniqueTestIdCount >= InwardPDfColumn
  //   ? renderVerticalLayout()
  //   : renderMatrixLayout();
};

export default InwardTestTableCommom;
